<template>
  <div class="flex min-h-0 flex-1 flex-col gap-4">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900">PO Activity</h1>
      <p class="m-0 text-sm text-gray-500">Every change made to a purchase order in the portal: scheduling, moves, quantities, notes.</p>
    </div>

    <ActivityFilterBar :filters="filters" :vendors="vendorOptions" :actors="actorOptions" :customers="customerOptions" :total="total" @update="applyFilters" />

    <PoActivityHeader
      v-if="filters.po"
      :po-number="filters.po"
      :vendor-name="poVendorName"
      :color="poColor"
      :schedule="poSchedule"
      :total="total"
      :loading="poLoading"
      :assignment-id="latestTouchedAssignmentId(events)"
    />

    <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ error }}
      <button type="button" class="ml-2 font-medium underline" @click="load()">Retry</button>
    </div>
    <div v-else-if="loading" class="card"><p class="m-0 text-sm text-gray-500">Loading changes…</p></div>
    <ActivityTimeline v-else :events="events" :has-more="nextCursor !== null" :loading-more="loadingMore" @load-more="loadMore" @select-po="applyFilters({ ...filters, po: $event })" />
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import ActivityFilterBar from '@/components/activity/ActivityFilterBar.vue';
  import ActivityTimeline from '@/components/activity/ActivityTimeline.vue';
  import PoActivityHeader from '@/components/activity/PoActivityHeader.vue';
  import { fetchCapacityTypes } from '@/services/capacityTypeService';
  import logger from '@/services/logger';
  import { fetchPoActivity, fetchPoActivityActors } from '@/services/poActivityService';
  import { useAccess } from '@/composables/useAccess';
  import { fetchPoSchedule } from '@/services/purchaseOrderService';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { useVendorStore } from '@/store/vendorStore';
  import { PERMISSIONS } from '@/types/access';
  import type { PoActivityEvent } from '@/types/poActivity';
  import { extractErrorMessage } from '@/utils/apiError';
  import { customerLabel, resolvePoSegmentColor, UNMAPPED_PO_COLOR } from '@/utils/customerColors';
  import { type ActivityFilters, filtersFromQuery, latestTouchedAssignmentId, queryFromFilters, scheduleByWeek, toApiQuery } from '@/utils/poActivity';
  import { vendorOptionLabel } from '@/utils/vendorArchive';

  const PAGE_SIZE = 50;

  const route = useRoute();
  const router = useRouter();
  const vendorStore = useVendorStore();
  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();
  const { hasPermission } = useAccess();

  // The URL is the single source of truth for filters, so the per-PO view is just ?po=… and
  // the board's menu can deep-link here.
  const filters = computed<ActivityFilters>(() => filtersFromQuery(route.query));

  const events = ref<PoActivityEvent[]>([]);
  const nextCursor = ref<string | null>(null);
  const total = ref<number | null>(null);
  const loading = ref(false);
  const loadingMore = ref(false);
  const error = ref<string | null>(null);
  const actors = ref<{ email: string; name: string }[]>([]);

  const poLoading = ref(false);
  const poVendorName = ref<string | null>(null);
  const poSchedule = ref<{ weekStart: string; portions: { typeCode: string; quantity: number }[] }[] | null>(null);
  const codeById = ref(new Map<string, string>());

  const vendorOptions = computed(() => vendorStore.vendors.map((vendor) => ({ value: vendor.id, label: vendorOptionLabel(vendor) })));
  const actorOptions = computed(() => actors.value.map((actor) => ({ value: actor.email, label: actor.name })));
  const customerOptions = computed(() =>
    privacyStore.hideCustomerMappings ? [] : mappingStore.items.map((mapping) => ({ value: mapping.code, label: customerLabel(mapping) })),
  );
  const poColor = computed(() =>
    filters.value.po
      ? resolvePoSegmentColor(filters.value.po, mappingStore.items, { hideCustomerMappings: privacyStore.hideCustomerMappings, index: 0 })
      : UNMAPPED_PO_COLOR,
  );
  // Computed once per filter change so the range's lower bound can't drift between the
  // first page and a later "load more" page.
  const apiQuery = computed(() => toApiQuery(filters.value, new Date()));

  // Stale responses (filters changed mid-flight) are dropped by comparing against this token.
  let requestToken = 0;

  async function load(): Promise<void> {
    const token = ++requestToken;
    loading.value = true;
    // A filter change always re-enables the button immediately, even if the in-flight
    // loadMore() it interrupted hasn't resolved yet.
    loadingMore.value = false;
    error.value = null;
    try {
      const page = await fetchPoActivity({ ...apiQuery.value, limit: PAGE_SIZE });
      if (token !== requestToken) return;
      events.value = page.items;
      nextCursor.value = page.nextCursor;
      total.value = page.total;
    } catch (err) {
      if (token !== requestToken) return;
      error.value = extractErrorMessage(err, 'Failed to load PO activity');
      // The counter sits beside the error card; a count from the previous filters would
      // describe results that are no longer on screen.
      total.value = null;
    } finally {
      if (token === requestToken) loading.value = false;
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value) return;
    const token = requestToken;
    loadingMore.value = true;
    try {
      const page = await fetchPoActivity({ ...apiQuery.value, cursor: nextCursor.value, limit: PAGE_SIZE });
      if (token !== requestToken) return;
      events.value = [...events.value, ...page.items];
      nextCursor.value = page.nextCursor;
    } catch (err) {
      if (token !== requestToken) return;
      logger.showError(extractErrorMessage(err, 'Failed to load older changes'));
    } finally {
      // Unconditional: this flag describes this call's own in-flight state, not the
      // page's data, so it must clear even when the response turned out to be stale.
      loadingMore.value = false;
    }
  }

  // The header's "currently scheduled" is the PO's own assignment rows — the same data the
  // board draws — read straight from the portal database, so the page works even while Odoo
  // is unreachable.
  async function loadPoHeader(poNumber: string): Promise<void> {
    const requested = poNumber;
    poLoading.value = true;
    poSchedule.value = null;
    poVendorName.value = null;
    try {
      const [schedule] = await Promise.all([fetchPoSchedule(poNumber), ensureCapacityTypes()]);
      if (requested !== filters.value.po) return;
      poVendorName.value = schedule.vendor?.alias ?? schedule.vendor?.name ?? null;
      // [] is a real answer here — the PO is not on the board — and reads as "Not scheduled";
      // null is reserved for a failed request ("Not available").
      poSchedule.value = scheduleByWeek(schedule.assignments, codeById.value);
    } catch (err) {
      logger.warn('PO header lookup failed', err);
      if (requested !== filters.value.po) return;
      poSchedule.value = null;
    } finally {
      // A newer call owns the flag once the PO has changed; it clears it in its own finally.
      if (requested === filters.value.po) poLoading.value = false;
    }
  }

  async function ensureCapacityTypes(): Promise<void> {
    if (codeById.value.size > 0) return;
    const types = await fetchCapacityTypes();
    codeById.value = new Map(types.map((type) => [type.id, type.code]));
  }

  function applyFilters(next: ActivityFilters): void {
    void router.push({ query: queryFromFilters(next) });
  }

  // route.query is a fresh object on every navigation, so a shallow watch already fires.
  watch(
    () => route.query,
    () => {
      void load();
    },
    { immediate: true },
  );

  // The header is a second, unrelated request: only the PO it describes should refetch it,
  // not a vendor or range change that leaves the same PO on screen.
  watch(
    () => filters.value.po,
    (po) => {
      if (po) void loadPoHeader(po);
    },
    { immediate: true },
  );

  onMounted(() => {
    // The page needs only read:po-activity; the vendor filter is an extra that would 403
    // without its own read permission. Mappings are open to every role (names redacted).
    if (hasPermission(PERMISSIONS.READ_VENDORS)) void vendorStore.fetchVendors();
    void mappingStore.load();
    fetchPoActivityActors()
      .then((list) => {
        actors.value = list;
      })
      .catch((err: unknown) => logger.warn('Actor list unavailable', err));
  });
</script>
